import { z } from "zod";
import { zWorldStyleFields, type WorldContext, type WorldRecord, type ContinuityDecision } from "./worlds";
import type { ImageReference } from "./images";
import { zWorldMomentPosition, type WorldMoment } from "./worldMoment";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
const world = { ownerAccountId: id, worldId: id };
const story = { storyAccountId: id, storyId: id, branchId: id.optional() };

/** Protected history operations use manageContent; projections use Firestore. */
export const zManageWorldRequest = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("inspectMoment"),
      ...world,
      position: zWorldMomentPosition.optional(),
      expectedRevision: z.number().int().nonnegative(),
    })
    .strict(),
  z.object({ action: z.literal("repairProjection"), ...world }).strict(),
  z.object({ action: z.literal("ensureDefault"), ownerAccountId: id }).strict(),
  z
    .object({
      action: z.literal("finalizeImage"),
      ...world,
      imagePath: z.string().min(1).max(2000),
      generation: z.string().regex(/^\d{1,40}$/),
    })
    .strict(),
  z
    .object({
      action: z.literal("createSharedAccount"),
      ownerAccountId: id,
      requestId: id,
      name: z.string().trim().min(1).max(120),
    })
    .strict(),
  z
    .object({
      action: z.literal("create"),
      ownerAccountId: id,
      requestId: id,
      name: z.string().trim().min(1).max(200),
      defaults: zWorldStyleFields.optional(),
      enhancedExtraction: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      action: z.literal("linkStory"),
      ...world,
      ...story,
      remember: z.boolean().default(true),
      entityMappings: z.record(id).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal("createStory"),
      ...world,
      requestId: id,
      title: z.string().trim().min(1).max(300),
      destinationAccountId: id.optional(),
      templateStoryId: id.optional(),
      entityIds: z.array(id).min(1).max(100).optional(),
      remember: z.boolean().default(true),
    })
    .strict(),
  z
    .object({
      action: z.literal("copyEntities"),
      ...world,
      destinationAccountId: id,
      destinationWorldId: id,
      requestId: id,
      entityIds: z.array(id).min(1).max(100),
    })
    .strict(),
  z
    .object({
      action: z.literal("remember"),
      ...world,
      requestId: id,
      proposalId: id,
      storyAccountId: id.optional(),
      storyId: id.optional(),
      sceneId: id.optional(),
    })
    .strict(),
  z.object({ action: z.literal("forget"), ...world, requestId: id, commitId: id }).strict(),
  z
    .object({
      action: z.literal("revisit"),
      ...world,
      requestId: id,
      decisionId: id,
      resolution: z.enum(["keep", "forget", "restore"]),
    })
    .strict(),
  z
    .object({
      action: z.literal("launchOpportunity"),
      ...world,
      requestId: id,
      opportunityId: id,
      storyAccountId: id.optional(),
      storyId: id.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal("setMember"),
      ...world,
      uid: id,
      memberAccountId: id.optional(),
      role: z.enum(["viewer", "contributor", "editor"]),
      revoked: z.boolean().default(false),
      entityIds: z.array(id).max(500).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal("fork"),
      ...world,
      destinationAccountId: id,
      requestId: id,
      name: z.string().trim().min(1).max(200),
      frontierEventIds: z.array(id).max(5000).optional(),
    })
    .strict(),
]);
export type ManageWorldRequest = z.input<typeof zManageWorldRequest>;
export type ParsedManageWorldRequest = z.infer<typeof zManageWorldRequest>;
export interface ManageWorldResponse {
  moment?: WorldMoment;
  accountId?: string;
  world?: WorldRecord;
  worldId?: string;
  image?: ImageReference;
  context?: WorldContext;
  storyAccountId?: string;
  storyId?: string;
  direction?: string;
  commitId?: string;
  decision?: ContinuityDecision;
  duplicate?: boolean;
  entityBindings?: Record<string, string>;
  blocked?: { message: string; eventIds: string[]; keys?: string[] };
}
